(ns ycweb.app.dashboard.planning.dashboard
  (:require
   [rum.core :as rum]
   [ycweb.app.admin.wholesale.common :as w.cmn]
   [ycweb.app.dashboard.planning.common :as planning.cmn]
   [ycweb.app.dashboard.planning.planners :as planners]
   [ycweb.app.dashboard.presence :as presence]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.datatables.datatables :as datatables]
   [ycweb.components.editor.base :as editor]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as yuc]
   [ycweb.util.urlstate :as urlstate]))

(def scope-options
  [{:ent/display-name "Categories"} {:ent/display-name "Suppliers"}])

;; Fixed at creation — the service ignores it on an update.
(def blank-field
  {:name :stockplan/blank
   :label "Blank plan"
   :type :checkbox
   :attr {:default false}})

(defn plan-scope
  "Which of the two scopes the form is on. A plan opened before the radio existed
   has no stored value, so an untouched form reads as Categories."
  [app-atom]
  (let [editor-atom (editor/editor-cursor app-atom :stockorder/oldplans)]
    (or (cmn/curact editor-atom [:form :stockplan/scope :value]) "Categories")))

(def old-plans-table-fns
  {:config-fns
   {:filter-rows (fn [_ rows] (filter #(= (:deleted %) 0) rows))}
   :column-fns
   {:planid-action
    (fn [app-atom _ _ planid]
      [:a
       (urlstate/href-props app-atom "planning-new" "plan" {:planid planid})
       "Plan"])}
   :editor
   {:form
    {:fields
     ;; Multi-select keeps its value as an id-keyed map of the chosen options —
     ;; form-submission-values turns that into the list the service receives.
     {:stockplan/scope
      {:change-fn
       ;; Switching scope drops what the other side held, so a plan can't leave
       ;; with both a supplier and a category set.
       (fn [_ editor-atom scope]
         (if (= scope "Suppliers")
           (swap! editor-atom assoc-in [:form :stockplan/categories :value] nil)
           (swap! editor-atom assoc-in [:form :stockplan/supplier :value] nil)))

       ;; The blank checkbox shares this cell, beside the radios, rather than
       ;; taking a grid cell of its own.
       :render-fn
       (fn [app-atom editor-cfg field]
         [:div {:class (yuc/bem :flex-grid-group [:flush-force :align-left])}
          [:div {:class (yuc/bem :flex-grid-block [:m-1-2])}
           [:div.flex-grid-content
            (editor/build-input-radio app-atom editor-cfg field)]]
          [:div {:class (yuc/bem :flex-grid-block [:m-1-2])}
           [:div.flex-grid-content
            (forms/field-label "Every product in scope at zero quantity, set on creation:" {})
            (editor/build-input-checkbox app-atom editor-cfg blank-field)]]])}

      ;; Kept out of the grid: the Plan By render-fn draws it. It is still a
      ;; form field, so it resets and submits like any other.
      :stockplan/blank
      {:hidden-fn (constantly true)}

      :stockplan/supplier
      {:hidden-fn (fn [app-atom] (not= (plan-scope app-atom) "Suppliers"))
       :search-fn #(planners/matches-all-terms? (:ent/display-name %1) %2)}

      :stockplan/categories
      {:hidden-fn (fn [app-atom] (not= (plan-scope app-atom) "Categories"))
       :search-fn #(planners/matches-all-terms? (:ent/display-name %1) %2)

       :select-fn
       (fn [_ input-atom {:keys [item-selector]} item]
         (let [id (get item item-selector)]
           (swap! input-atom update :value
             (fn [chosen]
               (if (contains? chosen id)
                 (dissoc chosen id)
                 (assoc chosen id (select-keys item [:ent/id :ent/display-name])))))))

       :item-render-fn
       (fn [_ input-atom {:keys [item-selector]} item]
         (let [chosen? (contains? (cmn/curact input-atom :value) (get item item-selector))]
           [:span (str (if chosen? "✓ " "") (:ent/display-name item))]))

       :display-fn
       (fn [_ chosen]
         (let [names (map :ent/display-name (vals chosen))]
           (case (count names)
             0 nil
             1 (first names)
             (str (count names) " categories selected"))))}
      :stockplan/effective-date
      {:hidden-fn
       (fn [app-atom]
         (let [editor-atom (editor/editor-cursor app-atom :stockorder/oldplans)
               plan-type (cmn/curact editor-atom [:form :stockplan/plantype :value])]
           (not= plan-type "Indent")))}}}}})

(def old-plans-table-config
  {:table-name :stockorder/oldplans
   :row-id :planid
   :data-source {:end-point :stockorder.planning/oldplans}
   :options
   {:order :desc
    :sort-title "Date Added"
    :filter :filter-rows
    :paging? false
    :pagination {:page 0 :page-size 20}}
   :buttons
   [{:text "Create Plan"
     :editor :stockorder/oldplans
     :extends :create}
    {:text "Edit Plan"
     :editor :stockorder/oldplans
     :extends :edit}
    {:text "Delete Plan"
     :editor :stockorder/oldplans
     :extends :remove}
    :refresh
    {:text "Bulk Commitment Upload"
     :action :planning-new/bulk.dashboard}
    {:text "Manage Suppliers"
     :action :planning-new/suppliers.dashboard}
    {:text "Manage Planners"
     :action :planning-new/planners.dashboard}
    {:text "Ringfencing"
     :action :planning-new/ringfencing}]
   :select? true
   :column-defs
   ;;TODO: Update :key values to be name spaced! example :stockplan/datadded
   [{:key :planid :type :selector}
    {:key :planid :title "Plan ID" :align-class :u-text-right}
    {:key :dateadded :title "Date Added" :type :date :default "N/A"}
    {:key :plancreatedby :title "Created By"}
    {:key :plantype :title "Plan Type"}
    {:key :effectivedate :title "Effective Date" :type :date :default "N/A"}
    {:key :filter :title "Filter"}
    {:key :totalvariations :title "Total Variations" :type :number}
    {:key :totalunits :title "Total Units Qty" :type :number}
    {:key :totalcost :title "Total Cost" :type :tocurrency}
    {:key :totalsuppliers :title "Total Suppliers" :type :number}
    {:key :totalbrands :title "Total Brands" :type :number}
    {:key :planid :title "Action" :render {:render-k :planid-action}}]})

(def old-plans-editor-config
  {:editor-name :stockorder/oldplans
   :datatable :stockorder/oldplans
   :mode :read
   ;; Eleven fields in one column runs past the bottom of the modal on a laptop.
   :style {:width :xlarge :columns 2}
   :end-points
   {:create :stockplan.planning/create
    :edit :stockplan.planning/update
    :remove :stockplan.planning/remove}
   :fields
   [{:name :stockplan/plantype
     :label "Plan Type:"
     :type :select
     :attr {:default "Order"}
     :opts-cfg {:lookup {:text :ent/display-name}}}
    {:name :stockplan/filter
     :label "Filter:"
     :type :select
     :attr {:default "Only Available"}
     :opts-cfg {:lookup {:text :ent/display-name}}}
    {:name :stockplan/scope
     :label "Plan By:"
     :type :radio
     :attr {:default "Categories"}
     :opts-cfg {:lookup {:text :ent/display-name}}}
    {:name :stockplan/supplier
     :label "Supplier:"
     :type :select-search
     :attr {:placeholder "All Suppliers"}
     :opts-cfg {:lookup {:value :ent/id :text :ent/display-name}}}
    {:name :stockplan/categories
     :label "Categories:"
     :type :select-search
     :attr {:placeholder "All Categories"}
     :opts-cfg {:multi-select true :lookup {:value :ent/id :text :ent/display-name}}}
    blank-field
    {:name :stockplan/include-ordergroups
     :label "Include Only Order Group:"
     :type :select-search
     :attr {:placeholder "- Select Order Group to Include -"}
     :opts-cfg {:lookup {:value :ordergroup :text :ordergroup}}}
    {:name :stockplan/exclude-ordergroups
     :label "Exclude Order Group:"
     :type :select-search
     :attr {:placeholder "- Select Order Group to Exclude -"}
     :opts-cfg {:lookup {:value :ordergroup :text :ordergroup}}}
    {:name :stockplan/budget
     :label "Budget:"
     :type :text
     :attr {:type :currency :placeholder "No budget"}}
    ;; Placeholders show what the service substitutes for an empty input.
    {:name :stockplan/period
     :label "Weeks:"
     :type :text
     :attr {:type :number :placeholder "1 (forecasts one week of sales)"}}
    {:name :stockplan/fillrate
     :label "Fillrate:"
     :type :text
     :attr {:type :number :placeholder 99.99}}
    {:name :stockplan/mincover
     :label "Min. Coverage (% of forecast a line must need to keep its supplier):"
     :type :text
     :attr {:type :number :placeholder "0 (keeps every supplier)"}}
    ;; :class keeps it out of the two-column grid — it's hidden on everything but
    ;; an Indent, and a hidden field still occupies its cell.
    {:name :stockplan/effective-date
     :label "Effective Date"
     :type :date
     :class "u-max-width-480"}
    {:name :stockplan/planid
     :type :hidden}]})

(defmethod comms/receive :stockorder.planning/oldplans
  [app-atom {:keys [plans plantypes filters suppliers ordergroups categories]}]
  (datatables/post-comms app-atom :stockorder/oldplans
    {:rows plans})
  (editor/post-comms app-atom :stockorder/oldplans
    {:options
     {:stockplan/plantype plantypes
      :stockplan/filter filters
      :stockplan/scope scope-options
      :stockplan/supplier suppliers
      :stockplan/categories categories
      :stockplan/include-ordergroups ordergroups
      :stockplan/exclude-ordergroups ordergroups}}))

(defmethod comms/receive :stockplan.planning/create-updates [app-atom e]
  ;; Creation reports into the editor modal's overlay, a re-run into the screen's.
  (toaster/update-overlay-message app-atom :modal/modal-loading {:text (:status e)})
  (toaster/update-overlay-message app-atom planning.cmn/loading-overlay-key {:text (:status e)}))

(defmethod comms/receive :stockplan.planning/create [app-atom e]
  (editor/post-submit app-atom :stockorder/oldplans e)
  ;; The row goes in before the transition so the plan screen finds it in the
  ;; datatable — it reads the selected plan from there, not from the URL.
  (when-let [row (and (not (:error e)) (:row e))]
    (datatables/post-comms app-atom :stockorder/oldplans {:rows [row]})
    (urlstate/redirect app-atom "planning-new" "plan" {:planid (:planid row)})))

(defmethod comms/receive :stockplan.planning/update [app-atom e]
  (editor/post-submit app-atom :stockorder/oldplans e))

(defmethod comms/receive :stockplan.planning/remove [app-atom e]
  (editor/post-submit app-atom :stockorder/oldplans e))

(rum/defc list-plans < rum/reactive [app-atom _]
  [:div
   [:div
    {:class (yuc/bem :flex-grid-group [:flush :align-left :valign-middle])}
    [:div
     {:class (yuc/bem :flex-grid-block [:fluid :flush :grow-yes])}
     [:div.flex-grid-content
      (headings/heading "Old plans" {:type :h2 :size :medium})]]
    [:div
     {:class (yuc/bem :flex-grid-block [:fluid])}
     [:div.flex-grid-content
      (buttons/button-web "Try new layout"
         (merge
           (w.cmn/try-new-layout-props)
           {:style :light
            :size :default}))]]]
   [:div.u-min-height-160
    (datatables/table app-atom :stockorder/oldplans old-plans-table-fns)]])

(defmethod flow/render-screen :admin.planning-new [app-atom _]
  (planning.cmn/with-loading-overlay app-atom
    (cmn/permission-lock app-atom "SiteAdmin"
      (list-plans app-atom []))))

(defmethod t/transition-open :admin.planning-new [app-atom _ params]
  (w.cmn/redirect-to-new-layout-if-needed! app-atom "planning" params))

(defmethod t/transition-after-open :admin.planning-new [app-atom _ _]
  (presence/open app-atom {:context/ident :stockorder.planning/dashboard})
  (datatables/reset-data app-atom old-plans-table-config {})
  (editor/initiate app-atom old-plans-editor-config))

(defmethod grid/action :planning-new/bulk.dashboard [app-atom _ _]
  (urlstate/redirect app-atom "planning-new" "bulk" {}))

(defmethod grid/action :planning-new/suppliers.dashboard [app-atom _ _]
  (urlstate/redirect app-atom "planning-new" "suppliers" {}))

(defmethod grid/action :planning-new/planners.dashboard [app-atom _ _]
  (urlstate/redirect app-atom "planning-new" "planners" {}))

;; A full page load rather than a screen transition: ringfencing is its own Vite
;; app, mounted by a PHP template. It has to be planning.htm — planning-new.htm
;; has a page template, so it renders the CLJS app and never reads ?action.
(defmethod grid/action :planning-new/ringfencing [app-atom _ _]
  (urlstate/raw-redirect app-atom "/admin/planning.htm?action=ringfencing"))
